import STORAGE_SECRET from 'constants/STORAGE_SECRET';
import CryptoJS from 'crypto-js';

function seed(state1, state2) {
  var mod1 = 4294967087
  var mul1 = 65539
  var mod2 = 4294965887
  var mul2 = 65537
  if (typeof state2 != 'number') {
    state2 = state1
  }
  state1 = state1 % (mod1 - 1) + 1
  state2 = state2 % (mod2 - 1) + 1
  function random(limit) {
    state1 = (state1 * mul1) % mod1
    state2 = (state2 * mul2) % mod2
    if (state1 < limit && state2 < limit && state1 < mod1 % limit && state2 < mod2 % limit) {
      return random(limit)
    }
    return (state1 + state2) % limit
  }
  return random
}

function sequence(s) {
  const random = seed(20240614);

  const n = s.length;
  const a = Array.from({ length: n }).map((_, index) => index);

  for (var i = n - 1; i >= 0; i--) {
    var j = random(n);
    var tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }

  return a;
}

function shuffle(s, q) {
  const a = s.split('')
  const n = a.length;

  for (var i = n - 1; 0 <= i; --i) {
    var j = q[n - 1 - i];
    var tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }

  return a.join('');
}

function restore(s, q) {
  const a = s.split('')
  const n = a.length;

  for (var i = 0; i < n; ++i) {
    var j = q[n - 1 - i];
    var tmp = a[j];
    a[j] = a[i];
    a[i] = tmp;
  }

  return a.join('');
}

export function dec(s, secret) {
  if (!s) return null;
  const q = sequence(s);
  const t = restore(s, q);
  const hex = CryptoJS.enc.Hex.parse(t);
  const base64 = CryptoJS.enc.Base64.stringify(hex);
  const decrypted = CryptoJS.AES.decrypt(base64, secret);
  const v = decrypted.toString(CryptoJS.enc.Utf8);
  return v;
}

export function enc(v, secret) {
  if (!v) return null;
  const encrypted = CryptoJS.AES.encrypt(v, secret);
  const base64 = encrypted.toString();
  const hex = CryptoJS.enc.Base64.parse(base64);
  const t = CryptoJS.enc.Hex.stringify(hex);
  const q = sequence(t);
  const s = shuffle(t, q);
  return s;
}

export function generate_aes_key(length = 32) {
  const key = [];
  const hex = '0123456789abcdef';

  for (let i = 0; i < length; i++) {
    key.push(hex.charAt(Math.floor(Math.random() * 16)));
  }
  return key.join('');
}

const pemHF = {
  public: {
    header: '-----BEGIN PUBLIC KEY-----',
    footer: '-----END PUBLIC KEY-----',
  },
  private: {
    header: '-----BEGIN RSA PRIVATE KEY-----',
    footer: '-----END RSA PRIVATE KEY-----',
  },
};


export function make_pem(keyType, base64) {
  const pemExported = pemHF[keyType].header + '\n' + base64 + '\n' + pemHF[keyType].footer;
  return pemExported;
}

export async function export_rsa_key(keyType, key) {
  const exported = await window.crypto.subtle.exportKey(keyType === 'public' ? 'spki' : 'pkcs8', key);
  const exportedAsString = String.fromCharCode.apply(null, new Uint8Array(exported));
  const exportedAsBase64 = window.btoa(exportedAsString);
  const pemExported = pemHF[keyType].header + '\n' + exportedAsBase64 + '\n' + pemHF[keyType].footer;
  return pemExported;
};

export async function create_rsa_keypair() {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );
  const publicKey = await export_rsa_key('public', keyPair.publicKey);
  const privateKey = await export_rsa_key('private', keyPair.privateKey);
  return { publicKey, privateKey };
}

function strToArrayBuffer(str) {
  const encoder = new TextEncoder();
  return encoder.encode(str).buffer;
};

function str2ab(str) {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return buf;
}

function arrayBufferToStr(buf) {
  return String.fromCharCode.apply(null, new Uint8Array(buf));
};

export async function encrypt_rsa_pem(text, publicKeyb64) {
  const base64 = publicKeyb64.replace(pemHF.public.footer, '').replace(pemHF.public.header, '').replace(/\n/g, '');
  const binaryDerString = window.atob(base64);
  const binaryDer = str2ab(binaryDerString);
  const publicKey = await window.crypto.subtle.importKey(
    'spki',
    binaryDer,
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: { name: 'SHA-256' },
    },
    true,
    ['encrypt']
  );
  const cipher = await window.crypto.subtle.encrypt(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: { name: 'SHA-256' },
    },
    publicKey,
    strToArrayBuffer(CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(text)))
  );
  return window.btoa(arrayBufferToStr(cipher));
};

export async function decrypt_rsa_pem(cipher, privateKeyb64) {
  const base64 = privateKeyb64.replace(pemHF.private.footer, '').replace(pemHF.private.header, '').replace(/\n/g, '');
  const binaryDerString = window.atob(base64);
  const binaryDer = str2ab(binaryDerString);
  const privateKey = await window.crypto.subtle.importKey(
    'pkcs8',
    binaryDer,
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: { name: 'SHA-256' },
    },
    true,
    ['decrypt']
  );
  const text = await window.crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: { name: 'SHA-256' },
    },
    privateKey,
    str2ab(atob(cipher))
  );
  return CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Base64.parse(arrayBufferToStr(text)));
};

export async function encrypt_rsa(text, publicKeyb64) {
  return await encrypt_rsa_pem(text, make_pem('public', publicKeyb64));
}

export async function decrypt_rsa(text, privateKeyb64) {
  return await decrypt_rsa_pem(text, make_pem('private', privateKeyb64));
}


export function encrypt_aes(text, key, iv) {
  // const base64 = CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(text));
  const encrypted = CryptoJS.AES.encrypt(text, CryptoJS.enc.Utf8.parse(key), { iv });
  const encryptedBase64 = encrypted.toString();
  return encryptedBase64;
}

export function decrypt_aes(encryptedBase64, key, iv) {
  const decrypted = CryptoJS.AES.decrypt(encryptedBase64, CryptoJS.enc.Utf8.parse(key), { iv });
  return decrypted.toString(CryptoJS.enc.Utf8);
}

export function encrypt_password(password, time) {
  return encrypt_aes(password, `${time}_khp_xrtwin0000000`, CryptoJS.enc.Utf8.parse('0000000000000000'));
}

export function decrypt_password(encrypted, time) {
  return decrypt_aes(encrypted, `${time}_khp_xrtwin0000000`, CryptoJS.enc.Utf8.parse('0000000000000000'));
}